import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const PasswordChangeCard = ({ themeColors, themeStyles, currentThemeName }) => {
  const { changePassword } = useAuth();
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

  // Cleanup function to reset loading state if component unmounts
  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear message when user makes changes
    if (message.text) {
      setMessage({ text: '', type: '' });
    }
    
    // Check password strength for new password field
    if (field === 'newPassword') {
      checkPasswordStrength(value);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = 'Mevcut şifre gerekli';
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'Yeni şifre gerekli';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Yeni şifre en az 8 karakter olmalı';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Lütfen yeni şifrenizi onaylayın';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'Yeni şifre mevcut şifreden farklı olmalı';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    let text = '';
    let color = '';

    if (password.length >= 8) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    switch (score) {
      case 0:
      case 1:
        text = 'Very Weak';
        color = themeColors.error || '#ef4444';
        break;
      case 2:
        text = 'Weak';
        color = themeColors.warning || '#f59e0b';
        break;
      case 3:
        text = 'Fair';
        color = themeColors.warning || '#f59e0b';
        break;
      case 4:
        text = 'Good';
        color = themeColors.success || '#22c55e';
        break;
      case 5:
        text = 'Strong';
        color = themeColors.success || '#22c55e';
        break;
      default:
        text = '';
        color = '';
    }

    setPasswordStrength({ score, text, color });
  };

  const resetForm = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
    setErrors({});
    setPasswordStrength({ score: 0, text: '', color: '' });
  };

  const resetFormAndMessage = () => {
    resetForm();
    setMessage({ text: '', type: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });
    setErrors({}); // Clear any existing errors

    // Add a timeout to prevent loading state from getting stuck
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setMessage({ text: t('common.requestTimedOut'), type: 'error' });
    }, 30000); // 30 second timeout

    try {
      const result = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      // Clear timeout since request completed
      clearTimeout(timeoutId);

      if (result.success) {
        // Show enhanced success message
        setMessage({ 
          text: '✅ Password changed successfully! Your new password is now active.', 
          type: 'success' 
        });
        
        // Clear form fields manually (without clearing the message)
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswords({
          current: false,
          new: false,
          confirm: false
        });
        setErrors({});
        setPasswordStrength({ score: 0, text: '', color: '' });
        
        // Auto-hide success message after 8 seconds (longer for success)
        setTimeout(() => {
          setMessage({ text: '', type: '' });
        }, 8000);
        
      } else {
        setMessage({ text: result.error, type: 'error' });
        // Auto-hide error message after 8 seconds
        setTimeout(() => {
          setMessage({ text: '', type: '' });
        }, 8000);
      }
    } catch (error) {
      // Clear timeout since request completed (with error)
      clearTimeout(timeoutId);
      
      console.error('Password change error:', error);
      setMessage({ text: t('common.unexpectedError'), type: 'error' });
      // Auto-hide error message after 8 seconds
      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 8000);
    } finally {
      // Always reset loading state, regardless of success or failure
      setLoading(false);
    }
  };

  const getInputStyle = (hasError) => ({
    width: '100%',
    padding: '12px 16px',
    border: `2px solid ${hasError ? themeColors.error || '#ef4444' : themeStyles.border}`,
    borderRadius: 8,
    fontSize: 14,
    background: currentThemeName === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'white',
    color: themeStyles.text,
    outline: 'none',
    transition: 'all 0.2s ease'
  });

  const getButtonStyle = (isPrimary = false) => ({
    padding: '12px 24px',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    background: isPrimary 
      ? themeStyles.primary
      : 'transparent',
    color: isPrimary ? 'white' : themeStyles.textSecondary,
    border: isPrimary ? 'none' : `2px solid ${themeStyles.border}`,
    opacity: 1
  });

  return (
    <div style={{
      background: themeStyles.cardBg,
      border: `1px solid ${themeStyles.border}`,
      borderRadius: 16,
      padding: 24,
      marginBottom: 20,
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(8px)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>
        {`
          @keyframes fadeInScale {
            0% { 
              opacity: 0; 
              transform: scale(0.95) translateY(-10px); 
            }
            100% { 
              opacity: 1; 
              transform: scale(1) translateY(0); 
            }
          }
        `}
      </style>

      {/* Background gradient overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, ${themeStyles.primary}05 0%, ${themeStyles.primary}02 100%)`,
        pointerEvents: 'none'
      }} />
      
      {/* Card Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 24,
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: 12,
          background: `${themeStyles.primary}15`,
          color: themeStyles.primary,
          border: `1px solid ${themeStyles.primary}30`
        }}>
          <Lock size={24} />
        </div>
        <div>
          <h3 style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            color: themeStyles.text
          }}>
            {t('common.changePassword')}
          </h3>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: 14,
            color: themeStyles.textSecondary
          }}>
                          {t('common.updatePasswordSecurely')}
          </p>
        </div>
      </div>



      {/* Message Display */}
      {message.text && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
                  background: message.type === 'success' 
          ? `${themeColors.success || '#22c55e'}15`
          : `${themeColors.error || '#ef4444'}15`,
        border: `1px solid ${message.type === 'success' 
          ? themeColors.success || '#22c55e'
          : themeColors.error || '#ef4444'}30`,
        color: message.type === 'success' 
          ? themeColors.success || '#22c55e'
          : themeColors.error || '#ef4444'
        }}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      {/* Password Change Form */}
      <form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 1 }}>
        {/* Form Status */}
        <div style={{
          padding: '12px 16px',
          marginBottom: 20,
          borderRadius: 8,
          background: `${themeStyles.primary}05`,
          border: `1px solid ${themeStyles.primary}20`,
          fontSize: 14,
          color: themeStyles.textSecondary
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Lock size={16} />
            <strong>{t('common.formStatus')}</strong>
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
            <li>{t('common.currentPasswordRequired')}</li>
            <li>{t('common.newPasswordMinLength')}</li>
            <li>{t('common.passwordsMustMatch')}</li>
            <li>{t('common.newPasswordDifferent')}</li>
          </ul>
        </div>
        {/* Current Password */}
        <div style={{ marginBottom: 20 }}>
          <label style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 500,
            color: themeStyles.text,
            marginBottom: 8
          }}>
            {t('common.currentPassword')}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPasswords.current ? 'text' : 'password'}
              value={formData.currentPassword}
              onChange={(e) => handleInputChange('currentPassword', e.target.value)}
              style={getInputStyle(!!errors.currentPassword)}
              placeholder={t('common.enterCurrentPassword')}
              disabled={loading}
              onFocus={(e) => {
                e.target.style.borderColor = errors.currentPassword ? (themeColors.error || '#ef4444') : themeStyles.primary;
                e.target.style.boxShadow = `0 0 0 3px ${errors.currentPassword ? (themeColors.error || '#ef4444') + '20' : themeStyles.primary + '20'}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.currentPassword ? (themeColors.error || '#ef4444') : themeStyles.border;
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('current')}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: themeStyles.textSecondary,
                cursor: 'pointer',
                padding: 4
              }}
            >
              {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.currentPassword && (
            <div style={{
              color: themeColors.error || '#ef4444',
              fontSize: 12,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              <AlertCircle size={12} />
              {errors.currentPassword}
            </div>
          )}
        </div>

        {/* New Password */}
        <div style={{ marginBottom: 20 }}>
                      <label style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 500,
            color: themeStyles.text,
            marginBottom: 8
          }}>
            {t('common.newPassword')}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPasswords.new ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              style={getInputStyle(!!errors.newPassword)}
              placeholder={t('common.enterNewPassword')}
              disabled={loading}
              onFocus={(e) => {
                e.target.style.borderColor = errors.newPassword ? (themeColors.error || '#ef4444') : themeStyles.primary;
                e.target.style.boxShadow = `0 0 0 3px ${errors.newPassword ? (themeColors.error || '#ef4444') + '20' : themeStyles.primary + '20'}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.newPassword ? (themeColors.error || '#ef4444') : themeStyles.border;
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: themeStyles.textSecondary,
                cursor: 'pointer',
                padding: 4
              }}
            >
              {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {/* Password Strength Indicator */}
          {formData.newPassword && (
            <div style={{
              marginTop: 8,
              padding: '8px 12px',
              borderRadius: 6,
              background: `${passwordStrength.color}15`,
              border: `1px solid ${passwordStrength.color}30`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{
                fontSize: 12,
                color: passwordStrength.color,
                fontWeight: 500
              }}>
                Password Strength: {passwordStrength.text}
              </span>
              <div style={{
                display: 'flex',
                gap: 2
              }}>
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: level <= passwordStrength.score ? passwordStrength.color : themeStyles.border,
                      transition: 'background 0.2s ease'
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          
          {errors.newPassword && (
            <div style={{
              color: themeColors.error || '#ef4444',
              fontSize: 12,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              <AlertCircle size={12} />
              {errors.newPassword}
            </div>
          )}
        </div>

        {/* Confirm New Password */}
        <div style={{ marginBottom: 24 }}>
                      <label style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 500,
            color: themeStyles.text,
            marginBottom: 8
          }}>
            {t('common.confirmNewPassword')}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              style={getInputStyle(!!errors.confirmPassword)}
              placeholder={t('common.confirmNewPasswordPlaceholder')}
              disabled={loading}
              onFocus={(e) => {
                e.target.style.borderColor = errors.confirmPassword ? (themeColors.error || '#ef4444') : themeStyles.primary;
                e.target.style.boxShadow = `0 0 0 3px ${errors.confirmPassword ? (themeColors.error || '#ef4444') + '20' : themeStyles.primary + '20'}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.confirmPassword ? (themeColors.error || '#ef4444') : themeStyles.border;
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: themeStyles.textSecondary,
                cursor: 'pointer',
                padding: 4
              }}
            >
              {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <div style={{
              color: themeColors.error || '#ef4444',
              fontSize: 12,
              marginTop: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              <AlertCircle size={12} />
              {errors.confirmPassword}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: 12,
          marginTop: 8
        }}>
          <button
            type="submit"
            disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
            style={{
              ...getButtonStyle(true),
              opacity: (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) ? 0.6 : 1,
              cursor: (loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword) ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!loading && formData.currentPassword && formData.newPassword && formData.confirmPassword) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {loading ? t('common.processing') : t('common.changePassword')}
          </button>
          
          <button
            type="button"
            onClick={resetForm}
            disabled={loading}
            style={{
              ...getButtonStyle(false),
              border: `2px solid ${themeStyles.border}`,
              background: 'transparent'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                e.target.style.background = `${themeStyles.primary}10`;
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
              e.target.style.background = 'transparent';
            }}
          >
            {t('common.resetForm')}
          </button>
          
          {/* Emergency reset button if loading gets stuck */}
          {loading && (
            <button
              type="button"
              onClick={() => {
                setLoading(false);
                setMessage({ text: 'Loading state reset. You can try again.', type: 'info' });
                setTimeout(() => setMessage({ text: '', type: '' }), 3000);
              }}
              style={{
                padding: '8px 16px',
                border: `2px solid ${themeColors.error || '#ef4444'}`,
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                background: 'transparent',
                color: themeColors.error || '#ef4444',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `${themeColors.error || '#ef4444'}15`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
              }}
            >
              Reset Loading
            </button>
          )}
        </div>
      </form>

      {/* Security Tips */}
      <div style={{
        marginTop: 24,
        padding: '16px',
        background: `${themeStyles.primary}05`,
        borderRadius: 8,
        border: `1px solid ${themeStyles.primary}20`
      }}>
                  <h4 style={{
            margin: '0 0 8px 0',
            fontSize: 14,
            fontWeight: 600,
            color: themeStyles.primary
          }}>
            {t('common.passwordSecurityTips')}
          </h4>
          <ul style={{
            margin: 0,
            paddingLeft: 20,
            fontSize: 12,
            color: themeStyles.textSecondary,
            lineHeight: 1.5
          }}>
            <li>{t('common.useAtLeast8Chars')}</li>
            <li>{t('common.includeMixLettersNumbers')}</li>
            <li>{t('common.dontReusePasswords')}</li>
            <li>{t('common.considerPasswordManager')}</li>
          </ul>
      </div>
    </div>
  );
};

export default PasswordChangeCard;
